import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { getErrorMessage } from '@/api/core/axiosErrorMapper';
import { SystemNotification } from '@/app/components/feedback/SystemNotification';
import { useUploadAnnouncementMedia } from '@/app/pages/notifications/announcements/core/_hooks';
import {
  AnnouncementMediaUploadStatus,
  type AnnouncementMediaEnqueue,
  type AnnouncementMediaSlot,
  type AnnouncementMediaUploadEntry,
  type AnnouncementMediaUploadFile,
  type AnnouncementMediaUploadOptions,
  type AnnouncementMediaUploads,
} from '@/app/pages/notifications/announcements/core/_models';
import {
  getAnnouncementBodyContent,
  getAnnouncementMediaFile,
  getAnnouncementMediaSlot,
  getAnnouncementMediaSources,
  removeAnnouncementMediaSlot,
  replaceAnnouncementMediaSlot,
} from '@/app/pages/notifications/announcements/core/_utils';

const MAX_CONCURRENT_UPLOADS: number = 3;

export const useAnnouncementMediaUploads = (options: AnnouncementMediaUploadOptions): AnnouncementMediaUploads => {
  const { mutateAsync: uploadMedia, reset: resetUpload } = useUploadAnnouncementMedia();
  const [entries, setEntries] = useState<AnnouncementMediaUploadEntry[]>([]);
  const filesRef = useRef(new Map<string, AnnouncementMediaUploadFile>());
  const controllersRef = useRef(new Map<string, AbortController>());
  const queuedIdsRef = useRef<string[]>([]);
  const sessionRef = useRef(0);
  const optionsRef = useRef(options);
  const bodyRef = useRef(options.body);
  optionsRef.current = options;
  bodyRef.current = options.body;

  const cancelUploads = useCallback(() => {
    sessionRef.current++;
    controllersRef.current.forEach((controller) => controller.abort());
    controllersRef.current.clear();
    filesRef.current.clear();
    queuedIdsRef.current = [];
    resetUpload();
  }, [resetUpload]);
  const reset = useCallback(() => {
    cancelUploads();
    setEntries([]);
  }, [cancelUploads]);

  useLayoutEffect(() => {
    reset();
    return cancelUploads;
  }, [options.active, options.sourceId, options.initialValues, reset, cancelUploads]);

  const changeBody = useCallback((body: string) => {
    bodyRef.current = body;
    optionsRef.current.onBodyChange(body);
  }, []);
  const hasSlot = useCallback((id: string) => {
    const bodyMediaSources = getAnnouncementMediaSources(bodyRef.current);
    const mediaSlot = getAnnouncementMediaSlot(id);
    return bodyMediaSources.includes(mediaSlot);
  }, []);
  const updateEntry = useCallback((id: string, changes: Partial<AnnouncementMediaUploadEntry>) => {
    setEntries((current) => current.map((entry) => (entry.id === id ? { ...entry, ...changes } : entry)));
  }, []);
  const removeEntry = useCallback((id: string) => {
    setEntries((current) => current.filter((entry) => entry.id !== id));
  }, []);
  const discard = useCallback(
    (id: string) => {
      controllersRef.current.get(id)?.abort();
      controllersRef.current.delete(id);
      filesRef.current.delete(id);
      queuedIdsRef.current = queuedIdsRef.current.filter((queuedId) => queuedId !== id);
      removeEntry(id);
    },
    [removeEntry],
  );

  const startUploads: () => void = useCallback(() => {
    if (!optionsRef.current.active) return;
    while (controllersRef.current.size < MAX_CONCURRENT_UPLOADS && queuedIdsRef.current.length > 0) {
      const id = queuedIdsRef.current.shift();
      if (!id) continue;
      const task = filesRef.current.get(id);
      if (!task || !hasSlot(id)) {
        discard(id);
        continue;
      }
      const controller = new AbortController();
      controllersRef.current.set(id, controller);
      updateEntry(id, { status: AnnouncementMediaUploadStatus.UPLOADING, error: undefined });
      const currentSession = () =>
        sessionRef.current === task.session && optionsRef.current.active && filesRef.current.has(id);
      const upload = uploadMedia({
        file: task.file,
        signal: controller.signal,
        onUploadProgress: (event) => {
          if (!currentSession()) return;
          const progress = event.total ? Math.round((event.loaded / event.total) * 100) : 0;
          updateEntry(id, { progress });
        },
      });
      void upload
        .then((attachment) => {
          if (!currentSession() || !hasSlot(id)) return;
          const publicUrl = attachment.public_url;
          const nextBody = publicUrl
            ? replaceAnnouncementMediaSlot(bodyRef.current, id, publicUrl)
            : removeAnnouncementMediaSlot(bodyRef.current, id);
          changeBody(nextBody);
          optionsRef.current.onMediaUploaded(attachment);
          filesRef.current.delete(id);
          removeEntry(id);
        })
        .catch((error: unknown) => {
          if (!currentSession() || !hasSlot(id)) return;
          const message = getErrorMessage(error);
          updateEntry(id, { status: AnnouncementMediaUploadStatus.FAILED, error: message });
        })
        .finally(() => {
          controllersRef.current.delete(id);
          startUploads();
        });
    }
  }, [changeBody, hasSlot, discard, removeEntry, updateEntry, uploadMedia]);

  useLayoutEffect(() => {
    // A mídia apagada no editor leva junto o envio dela.
    const bodyMediaSources = getAnnouncementMediaSources(options.body);
    const sources = new Set(bodyMediaSources);
    for (const id of filesRef.current.keys()) {
      const mediaSlot = getAnnouncementMediaSlot(id);
      if (!sources.has(mediaSlot)) discard(id);
    }
    startUploads();
  }, [options.body, discard, startUploads]);

  // O editor insere as mídias devolvidas; o envio começa quando elas chegam ao corpo, pelo efeito acima.
  const enqueue: AnnouncementMediaEnqueue = (files) => {
    if (!optionsRef.current.active) return [];
    const added: AnnouncementMediaUploadEntry[] = [];
    const slots: AnnouncementMediaSlot[] = [];
    for (const original of files) {
      try {
        const { file, type } = getAnnouncementMediaFile(original);
        const id = crypto.randomUUID();
        filesRef.current.set(id, { file, session: sessionRef.current });
        queuedIdsRef.current.push(id);
        added.push({ id, filename: file.name, status: AnnouncementMediaUploadStatus.QUEUED, progress: 0 });
        const mediaSlot = getAnnouncementMediaSlot(id);
        slots.push({ type, src: mediaSlot, alt: file.name });
      } catch (error: unknown) {
        const errorMessage = getErrorMessage(error);
        SystemNotification.error(errorMessage);
      }
    }
    if (added.length === 0) return [];
    setEntries((current) => [...current, ...added]);
    return slots;
  };

  const retry = (id: string) => {
    const isQueuedOrUploading = controllersRef.current.has(id) || queuedIdsRef.current.includes(id);
    const canRetry = optionsRef.current.active && filesRef.current.has(id) && hasSlot(id) && !isQueuedOrUploading;
    if (!canRetry) return;
    queuedIdsRef.current.push(id);
    updateEntry(id, { status: AnnouncementMediaUploadStatus.QUEUED, progress: 0, error: undefined });
    startUploads();
  };
  const remove = (id: string) => {
    const nextBody = removeAnnouncementMediaSlot(bodyRef.current, id);
    changeBody(nextBody);
    discard(id);
    startUploads();
  };
  const content = getAnnouncementBodyContent(options.body);
  return { entries, enqueue, retry, remove, reset, isBlocked: content.hasPendingMedia };
};
